<div class="invoice-modal">
  @if (isLoading) {
    <div class="invoice-modal__loading invoice-modal__no-print" role="status" aria-live="polite">
      {{ 'MESSAGES.LOADING' | translate }}
    </div>
  } @else if (pageErrorKey) {
    <div class="invoice-modal__error invoice-modal__no-print">
      <h2 class="invoice-modal__error-title">{{ 'ERRORS.UNKNOWN_ERROR' | translate }}</h2>
      <p class="invoice-modal__error-message">{{ pageErrorKey | translate }}</p>
      <div class="invoice-modal__error-actions">
        <button type="button" class="form-secondary-btn" (click)="requestClose()">
          {{ 'BUTTONS.CANCEL' | translate }}
        </button>
        <button type="button" class="form-primary-btn" (click)="retry()">
          {{ 'BUTTONS.RETRY' | translate }}
        </button>
      </div>
    </div>
  } @else if (headerView$ | async; as header) {
    <article class="invoice-modal__sheet flex flex-col gap-5">
      @if (actionErrorKey) {
        <p class="invoice-modal__action-error invoice-modal__no-print" role="alert">
          {{ actionErrorKey | translate }}
        </p>
      }

      <!--
        Print-only report block. On screen this is hidden via .invoice-modal__print-only.
        Inside @media print the on-screen header is hidden via .invoice-modal__no-print so the
        printed page shows only the simple report layout below.
      -->
      <section class="invoice-modal__print-report invoice-modal__print-only">
        <header class="invoice-modal__print-report-header">
          <h1 class="invoice-modal__print-report-title">
            {{ 'COMPONENTS.INVOICES.PRINT.HEADING' | translate }}
          </h1>
          <p class="invoice-modal__print-report-number">{{ header.number }}</p>
        </header>

        <dl class="invoice-modal__print-report-grid">
          <div>
            <dt>{{ 'COMPONENTS.INVOICES.FIELDS.CUSTOMER' | translate }}</dt>
            <dd>{{ header.customerName }}</dd>
          </div>
          @if (header.salesOrderNumber) {
            <div>
              <dt>{{ 'COMPONENTS.INVOICES.FIELDS.SALES_ORDER' | translate }}</dt>
              <dd>{{ header.salesOrderNumber }}</dd>
            </div>
          }
          <div>
            <dt>{{ 'COMPONENTS.INVOICES.FIELDS.INVOICE_DATE' | translate }}</dt>
            <dd>{{ header.invoiceDate | localizedDate }}</dd>
          </div>
          <div>
            <dt>{{ 'COMPONENTS.INVOICES.FIELDS.DUE_DATE' | translate }}</dt>
            <dd>{{ header.dueDate | localizedDate }}</dd>
          </div>
          <div>
            <dt>{{ 'COMPONENTS.INVOICES.FIELDS.STATUS' | translate }}</dt>
            <dd>{{ header.statusLabelKey | translate }}</dd>
          </div>
        </dl>
      </section>

      <header class="invoice-modal__header invoice-modal__no-print">
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-3 flex-wrap">
            <h1 class="invoice-modal__title m-0">{{ header.number }}</h1>
            <span
              class="invoice-modal__status-badge"
              [ngClass]="header.statusBadgeClass"
            >
              {{ header.statusLabelKey | translate }}
            </span>
          </div>

          @if (header.salesOrderId) {
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-sm font-medium text-neutral-700 dark:text-neutral-300 tabular-nums">
                {{ header.salesOrderNumber }}
              </span>
              <span class="invoice-modal__link-badge">
                {{ 'COMPONENTS.INVOICES.DETAIL.LINKED_BADGE' | translate }}
              </span>
            </div>
          }
        </div>

        <dl class="invoice-modal__header-meta">
          <div>
            <dt>{{ 'COMPONENTS.INVOICES.FIELDS.CUSTOMER' | translate }}</dt>
            <dd>{{ header.customerName }}</dd>
          </div>
          <div>
            <dt>{{ 'COMPONENTS.INVOICES.FIELDS.INVOICE_DATE' | translate }}</dt>
            <dd>{{ header.invoiceDate | localizedDate }}</dd>
          </div>
          <div>
            <dt>{{ 'COMPONENTS.INVOICES.FIELDS.DUE_DATE' | translate }}</dt>
            <dd>{{ header.dueDate | localizedDate }}</dd>
          </div>
        </dl>
      </header>

      @if (lineViews$ | async; as lineViews) {
        <div class="invoice-modal__lines-wrap">
          <table class="invoice-modal__lines">
            <thead>
              <tr>
                <th class="invoice-modal__line-cell--text">
                  {{ 'COMPONENTS.INVOICES.FIELDS.DESCRIPTION' | translate }}
                </th>
                <th class="invoice-modal__line-cell--right">
                  {{ 'COMPONENTS.INVOICES.FIELDS.QUANTITY' | translate }}
                </th>
                <th class="invoice-modal__line-cell--right">
                  {{ 'COMPONENTS.INVOICES.FIELDS.UNIT' | translate }}
                </th>
                <th class="invoice-modal__line-cell--right">
                  {{ 'COMPONENTS.INVOICES.FIELDS.PRICE' | translate }}
                </th>
                <th class="invoice-modal__line-cell--right">
                  {{ 'COMPONENTS.INVOICES.FIELDS.TAX_PERCENT' | translate }}
                </th>
                <th class="invoice-modal__line-cell--right">
                  {{ 'COMPONENTS.INVOICES.DETAIL.AMOUNT_EXCL' | translate }}
                </th>
                <th class="invoice-modal__line-cell--right">
                  {{ 'COMPONENTS.INVOICES.DETAIL.TAX' | translate }}
                </th>
                <th class="invoice-modal__line-cell--right">
                  {{ 'COMPONENTS.INVOICES.DETAIL.AMOUNT_INCL' | translate }}
                </th>
              </tr>
            </thead>
            <tbody>
              @for (line of lineViews; track line.id) {
                <tr>
                  <td class="invoice-modal__line-cell--text">{{ line.description || '-' }}</td>
                  <td class="invoice-modal__line-cell--right">{{ line.quantity | number: '1.0-2' }}</td>
                  <td class="invoice-modal__line-cell--right">{{ line.unitOfMeasureId }}</td>
                  <td class="invoice-modal__line-cell--right">{{ line.unitPrice | number: '1.2-2' }}</td>
                  <td class="invoice-modal__line-cell--right">{{ line.taxPercent | number: '1.0-2' }}</td>
                  <td class="invoice-modal__line-cell--right">{{ line.amountExcludingTax | number: '1.2-2' }}</td>
                  <td class="invoice-modal__line-cell--right">{{ line.totalTaxAmount | number: '1.2-2' }}</td>
                  <td class="invoice-modal__line-cell--right">{{ line.amountIncludingTax | number: '1.2-2' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      <section class="invoice-modal__totals">
        <div class="invoice-modal__total-row">
          <span>{{ 'COMPONENTS.INVOICES.DETAIL.SUBTOTAL' | translate }}</span>
          <span>{{ header.subtotal | number: '1.2-2' }}</span>
        </div>
        <div class="invoice-modal__total-row">
          <span>{{ 'COMPONENTS.INVOICES.DETAIL.TAX_TOTAL' | translate }}</span>
          <span>{{ header.taxTotal | number: '1.2-2' }}</span>
        </div>
        <div class="invoice-modal__total-row invoice-modal__total-row--accent">
          <span>{{ 'COMPONENTS.INVOICES.DETAIL.TOTAL' | translate }}</span>
          <span>{{ header.total | number: '1.2-2' }}</span>
        </div>
      </section>

      <div class="invoice-modal__footer-actions invoice-modal__no-print">
        @if (header.canDelete) {
          <button
            type="button"
            class="invoice-modal__btn--destructive"
            [disabled]="isMutating"
            (click)="openDeletePrompt()"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 7V4a1 1 0 011-1h2a1 1 0 011 1v3" />
            </svg>
            {{ (isMutating ? 'COMPONENTS.INVOICES.BUTTONS.DELETING' : 'COMPONENTS.INVOICES.BUTTONS.DELETE') | translate }}
          </button>
        }
        @if (header.canMarkPaid) {
          <button
            type="button"
            class="invoice-modal__btn--success"
            [disabled]="isMutating"
            (click)="openMarkPaidPrompt()"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            {{ (isMutating ? 'COMPONENTS.INVOICES.BUTTONS.MARKING_PAID' : 'COMPONENTS.INVOICES.BUTTONS.MARK_PAID') | translate }}
          </button>
        }
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
          (click)="requestClose()"
        >
          {{ 'BUTTONS.CANCEL' | translate }}
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          (click)="print()"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
            />
          </svg>
          {{ 'COMPONENTS.INVOICES.BUTTONS.PRINT' | translate }}
        </button>
      </div>
    </article>
  }
</div>

<app-reusable-modal
  [isOpen]="isMarkPaidPromptOpen"
  [panelClass]="'max-w-md'"
  [contentScoped]="true"
  [title]="'COMPONENTS.INVOICES.MODAL.MARK_PAID_TITLE' | translate"
  (closed)="closeMarkPaidPrompt()"
>
  <div class="invoice-modal__prompt">
    <p>{{ 'COMPONENTS.INVOICES.MODAL.MARK_PAID_PROMPT' | translate }}</p>
    <div class="invoice-modal__prompt-actions">
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        [disabled]="isMutating"
        (click)="closeMarkPaidPrompt()"
      >
        {{ 'BUTTONS.CANCEL' | translate }}
      </button>
      <button
        type="button"
        class="invoice-modal__btn--success"
        [disabled]="isMutating"
        (click)="confirmMarkPaid()"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        {{ (isMutating ? 'COMPONENTS.INVOICES.BUTTONS.MARKING_PAID' : 'COMPONENTS.INVOICES.BUTTONS.MARK_PAID') | translate }}
      </button>
    </div>
  </div>
</app-reusable-modal>

<app-reusable-modal
  [isOpen]="isDeletePromptOpen"
  [panelClass]="'max-w-md'"
  [contentScoped]="true"
  [title]="'COMPONENTS.INVOICES.MODAL.DELETE_TITLE' | translate"
  (closed)="closeDeletePrompt()"
>
  <div class="invoice-modal__prompt">
    <p>{{ 'COMPONENTS.INVOICES.MODAL.DELETE_PROMPT' | translate }}</p>
    <div class="invoice-modal__prompt-actions">
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        [disabled]="isMutating"
        (click)="closeDeletePrompt()"
      >
        {{ 'BUTTONS.CANCEL' | translate }}
      </button>
      <button
        type="button"
        class="invoice-modal__btn--destructive"
        [disabled]="isMutating"
        (click)="confirmDelete()"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 7V4a1 1 0 011-1h2a1 1 0 011 1v3" />
        </svg>
        {{ (isMutating ? 'COMPONENTS.INVOICES.BUTTONS.DELETING' : 'COMPONENTS.INVOICES.BUTTONS.DELETE') | translate }}
      </button>
    </div>
  </div>
</app-reusable-modal>
